import React, { useEffect, useState } from 'react';
import api from '../api/client';
import ProductPicker from '../components/ProductPicker';
import ServicePicker from '../components/ServicePicker';
import { useToast } from '../hooks/useToast';

const QuickQuote: React.FC = () => {
  const { showToast } = useToast();
  const [services, setServices] = useState<any[]>([]);
  const [parts, setParts] = useState<any[]>([]);
  const [labour, setLabour] = useState<any[]>([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [svcId, setSvcId] = useState('');

  useEffect(() => {
    api.get('/products', { params: { limit: 500 } })
      .then(r => setServices((r.data.data || []).filter((p: any) => p.product_type === 'service')))
      .catch(() => {});
  }, []);

  const fmt = (n: any) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const addPart = (p: any) => {
    const stock = (p.stock || []).reduce((s: number, x: any) => s + Number(x.qty || 0), 0);
    setParts(l => [...l, { id: p.id, name: p.name, price: String(p.selling_price ?? 0), qty: '1', stock }]);
    setPickerKey(k => k + 1);
  };
  const addService = (id: string) => {
    const s = services.find((x: any) => String(x.id) === id);
    if (s) setLabour(l => [...l, { id: s.id, name: s.name, price: String(s.selling_price ?? 0) }]);
    setSvcId('');
  };

  const partsTotal = parts.reduce((s, p) => s + Number(p.qty || 0) * Number(p.price || 0), 0);
  const labourTotal = labour.reduce((s, l) => s + Number(l.price || 0), 0);
  const total = partsTotal + labourTotal;

  const copy = () => {
    const lines = [
      'KLIK Technology — Qiimo qiyaas ah / Estimated price',
      ...parts.map(p => `${p.name}${Number(p.qty) > 1 ? ' x' + p.qty : ''}: $${fmt(Number(p.qty) * Number(p.price))}`),
      ...labour.map(l => `${l.name}: $${fmt(l.price)}`),
      `Wadar / Total: $${fmt(total)}`,
      'Qiimahan waa qiyaas; baaris kadib ayaa la xaqiijinayaa. / Estimate only, confirmed after diagnosis.',
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => showToast('Copied — paste into WhatsApp')).catch(() => showToast('Copy failed', 'error'));
  };

  const clear = () => { setParts([]); setLabour([]); };

  const th: React.CSSProperties = { padding: '6px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase' };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Quick Quote</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={clear}>Clear</button>
          <button className="btn btn-primary" onClick={copy} disabled={total <= 0}>Copy for WhatsApp</button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 12 }}>
        Price check only — nothing is saved and no stock is reserved.
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header"><span>Parts</span></div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--sur2)' }}>
            <th style={{ ...th, textAlign: 'left' }}>Part</th>
            <th style={{ ...th, textAlign: 'right' }}>In stock</th>
            <th style={{ ...th, textAlign: 'right' }}>Qty</th>
            <th style={{ ...th, textAlign: 'right' }}>Price</th>
            <th style={{ ...th, textAlign: 'right' }}>Total</th>
            <th style={{ width: 30 }}></th>
          </tr></thead>
          <tbody>
            {parts.map((p, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--bdr)' }}>
                <td style={{ padding: '7px 10px', fontSize: 12 }}>{p.name}</td>
                <td className="td-num" style={{ padding: '7px 10px', color: p.stock > 0 ? 'var(--green)' : 'var(--red)' }}>{p.stock > 0 ? p.stock : 'None'}</td>
                <td style={{ padding: '4px', width: 70 }}><input className="form-control mono" style={{ textAlign: 'right' }} value={p.qty} onChange={e => setParts(l => l.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} /></td>
                <td style={{ padding: '4px', width: 100 }}><input className="form-control mono" style={{ textAlign: 'right' }} value={p.price} onChange={e => setParts(l => l.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} /></td>
                <td className="td-num" style={{ padding: '7px 10px' }}>{fmt(Number(p.qty) * Number(p.price))}</td>
                <td style={{ textAlign: 'center' }}><button className="btn btn-secondary btn-sm" style={{ padding: '2px 6px' }} onClick={() => setParts(l => l.filter((_, j) => j !== i))}>x</button></td>
              </tr>
            ))}
            <tr><td colSpan={6} style={{ padding: '6px' }}>
              <ProductPicker key={pickerKey} value="" fontSize={12} onSelect={addPart} />
            </td></tr>
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header"><span>Labour</span></div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {labour.map((l, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--bdr)' }}>
                <td style={{ padding: '7px 10px', fontSize: 12 }}>{l.name}</td>
                <td style={{ padding: '4px', width: 100 }}><input className="form-control mono" style={{ textAlign: 'right' }} value={l.price} onChange={e => setLabour(x => x.map((y, j) => j === i ? { ...y, price: e.target.value } : y))} /></td>
                <td style={{ width: 30, textAlign: 'center' }}><button className="btn btn-secondary btn-sm" style={{ padding: '2px 6px' }} onClick={() => setLabour(x => x.filter((_, j) => j !== i))}>x</button></td>
              </tr>
            ))}
            <tr><td colSpan={3} style={{ padding: '6px' }}>
              <ServicePicker services={services} placeholder="Add a service — type to search" onSelect={(s: any) => addService(String(s.id))} />
            </td></tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-body" style={{ display: 'flex', justifyContent: 'flex-end', gap: 28 }}>
          <div style={{ textAlign: 'right' }}><div style={{ fontSize: 11, color: 'var(--tx3)' }}>Parts</div><div style={{ fontSize: 14, fontFamily: 'monospace' }}>{fmt(partsTotal)}</div></div>
          <div style={{ textAlign: 'right' }}><div style={{ fontSize: 11, color: 'var(--tx3)' }}>Labour</div><div style={{ fontSize: 14, fontFamily: 'monospace' }}>{fmt(labourTotal)}</div></div>
          <div style={{ textAlign: 'right' }}><div style={{ fontSize: 11, color: 'var(--tx3)' }}>Estimated total</div><div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'monospace' }}>{fmt(total)}</div></div>
        </div>
      </div>
    </div>
  );
};

export default QuickQuote;
